import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import TokenProvider from '@/context/TokenContext';
import CartProvider from '@/app/cart/CartContext';
import AvailabilityProvider from '@/app/availability/AvailabilityContext';

import BookingListPage from '@/app/bookings/BookingListPage';
import CoachBookingViewPage from '@/app/bookings/CoachBookingViewPage';
import ClientBookingViewPage from '@/app/bookings/ClientBookingViewPage';
import FavouritesList from '@/app/favourites/List';
import ConversationList from '@/app/conversation/List';
import ConversationView from '@/app/conversation/View';
import Settings from '@/app/settings/SettingsLoader';
import UserProfilePage from '@/app/user/UserProfilePage';

import Home from '@/app/home/Home';
import AvailabilityPage from '@/app/availability/AvailabilityPage';
import AvailabilityDayPage from '@/app/availability/AvailabilityDayPage';
import SignupPage from '@/app/user/SignupPage';
import CartViewPage from '@/app/cart/CartViewPage';
import ForgotPasswordPage from '@/app/user/ForgotPasswordPage';
import ResetPasswordPage from '@/app/user/ResetPasswordPage';
import CheckoutPageOld from '@/app/checkout/CheckoutPage__OLD';
import CheckoutPage from '@/app/checkout/CheckoutPage';
import CheckoutRouter from '@/app/checkout/CheckoutRouter';
import OrderPage from '@/app/order/OrderPage';
import EditCoachPage from '@/app/coach/EditCoachPage';
import AddCoachPage from '@/app/coach/AddCoachPage';
import CoachViewPage from '@/app/coach/CoachViewPage';
import CoachActivityQualificationsPage from '@/app/coachActivity/CoachActivityQualificationsPage';
import AddApplicationPage from '@/app/application/AddApplicationPage';
import EditApplicationPage from '@/app/application/EditApplicationPage';
import EditCoachProfilePage from '@/app/coach/EditCoachProfilePage';
import EditCoachProfileApplicationPage from '@/app/coach/EditCoachProfileApplicationPage';
import CoachListPage from '@/app/coach/CoachListPage';
import ErrorFallback from './ErrorFallback';
import PaymentConfirmationPage from '@/app/payment/PaymentConfirmationPage';
import CoachIdentificationPage from '@/app/coach/CoachIdentificationPage';
import CoachFirstAidPage from '@/app/coach/CoachFirstAidPage';
import CoachActivityContractorAgreementPage from '@/app/coachActivity/CoachActivityContractorAgreementPage';
import CoachActivityLocationsPage from '@/app/coachActivity/CoachActivityLocationsPage';
import CoachActivityWaiverPage from '@/app/coachActivity/CoachActivityWaiverPage';
import CoachActivityOfferingsPage from '@/app/coachActivity/CoachActivityOfferingsPage';
import AddOfferingPage from '@/app/offering/AddOfferingPage';
import EditOfferingPage from '@/app/offering/EditOfferingPage';
import ListOfferingsPage from '@/app/offering/ListOfferingsPage';
import AddOfferingSessionRuleSetPage from '@/app/offeringSessionRuleSet/AddOfferingSessionRuleSetPage';
import EditOfferingSessionRuleSetPage from '@/app/offeringSessionRuleSet/EditOfferingSessionRuleSetPage';
import CardLinkRedirectPage from '@/app/card/CardLinkRedirectPage';
import CardPage from '@/app/card/CardPage';
import TipPage from '@/app/tip/TipPage';
import ReviewPage from '@/app/review/ReviewPage';

import './index.css';
import App from './App.jsx';
// eslint-disable-next-line no-undef
if (process.env.VITE_NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://ba16214c14229f36cec37abfacd4ecb2@o4508229293703168.ingest.us.sentry.io/4508255264440320',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration()
    ],
    // Tracing
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api.profoundpros\.com\/v1/
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Capture all errors
    sampleRate: 1.0,
    // Enable source maps
    enableTracing: true
  });
}

if (navigator.serviceWorker) {
  navigator.serviceWorker.register('/service-worker.js');
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorFallback />,
    children: [
      {
        index: true,
        element: <Home />
      },
      {
        path: 'availability',
        element: <AvailabilityPage />
      },
      {
        path: 'availability/:date',
        element: <AvailabilityDayPage />
      },
      {
        path: 'favourites',
        element: <FavouritesList />
      },
      {
        path: 'booking',
        element: <BookingListPage />
      },
      {
        path: 'booking/:id/coach',
        element: <CoachBookingViewPage />
      },
      {
        path: 'booking/:id/client',
        element: <ClientBookingViewPage />
      },
      {
        path: 'cart/:id',
        element: <CartViewPage />
      },
      {
        path: 'checkout/cart/:cartId/step',
        element: <CheckoutPage />,
        children: [
          {
            path: ':stepKey',
            element: <CheckoutRouter />
          }
        ]
      },
      {
        path: 'checkout/cart/customer',
        element: <CheckoutPageOld />
      },
      {
        path: 'order/:id',
        element: <OrderPage />
      },
      {
        path: 'coach',
        element: <CoachListPage />
      },
      {
        path: 'conversation',
        element: <ConversationList />
      },
      {
        path: 'conversation/:id',
        element: <ConversationView />
      },
      {
        path: 'setting',
        element: <Settings />
      },
      {
        path: 'signup',
        element: <SignupPage />
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />
      },
      {
        path: 'reset-password/:resetPasswordToken',
        element: <ResetPasswordPage />
      },
      {
        path: 'setting/profile',
        element: <UserProfilePage />
      },
      {
        path: 'coach/profile',
        element: <EditCoachProfilePage />
      },
      {
        path: 'coach/:id',
        element: <CoachViewPage />
      },
      {
        path: 'payment-confirmation/:cartId',
        element: <PaymentConfirmationPage />
      },
      // Application V2
      {
        path: 'application/add',
        element: <AddApplicationPage />
      },
      {
        path: 'application/:coachActivityId',
        element: <EditApplicationPage />
      },
      {
        path: 'application/coach/add',
        element: <AddCoachPage />
      },
      {
        path: 'application/coach/:id',
        element: <EditCoachPage />
      },
      {
        path: 'application/identification/:coachId',
        element: <CoachIdentificationPage />
      },
      {
        path: 'application/first-aid/:coachId',
        element: <CoachFirstAidPage />
      },
      {
        path: 'application/qualifications/:coachActivityId',
        element: <CoachActivityQualificationsPage />
      },
      {
        path: 'application/contractor-agreement/:coachActivityId',
        element: <CoachActivityContractorAgreementPage />
      },
      {
        path: 'application/locations/:coachActivityId',
        element: <CoachActivityLocationsPage />
      },
      {
        path: 'application/offerings/:coachActivityId',
        element: <CoachActivityOfferingsPage />
      },
      {
        path: 'application/waiver/:coachActivityId',
        element: <CoachActivityWaiverPage />
      },
      {
        path: 'application/profile/:coachActivityId',
        element: <EditCoachProfileApplicationPage />
      },

      // Offering
      {
        path: 'offering/add',
        element: <AddOfferingPage />
      },
      {
        path: 'offering/:id',
        element: <EditOfferingPage />
      },
      {
        path: 'offering',
        element: <ListOfferingsPage />
      },

      // Offering Session Rules
      {
        path: 'offering-session-rule-set/:offeringId/add',
        element: <AddOfferingSessionRuleSetPage />
      },
      {
        path: 'offering-session-rule-set/:offeringId/edit/:offeringSessionRuleSetId',
        element: <EditOfferingSessionRuleSetPage />
      },

      // Card Pages
      {
        path: 'card-link/:hash',
        element: <CardLinkRedirectPage />
      },
      {
        path: 'card',
        element: <CardPage />
      },
      // Tip
      {
        path: 'tip/coach/:coachId',
        element: <TipPage />
      },
      // Review
      {
        path: 'review/coach/:coachId',
        element: <ReviewPage />
      }
    ]
  }
]);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <TokenProvider>
      <CartProvider>
        <AvailabilityProvider>
          <RouterProvider router={router} />
        </AvailabilityProvider>
      </CartProvider>
    </TokenProvider>
  </React.StrictMode>
);
